import React, { useState } from 'react'
import PropTypes from 'prop-types'

import { paymentLabel, direction } from '../constants'
import { standardPlan } from '../../price-table/constants'
import useStyles from '../styles'
import useChangeSubscription from '../hooks/use-change-subscription'
import usePassword from '../hooks/use-password'
import PaymentMethodHeader from '../components/payment-method-header'
import { LoadingComponent } from '../../../components/loading'
import CreditCardChangeSubscription from '../components/credit-card-change-subscription'
import IframeChangeSubscription from '../components/iframe-change-subscription'
import { planType } from '../../shared/subscription-constants'

const Payment = ({ product, changeDirection, voucher }) => {
  const styles = useStyles()
  const [isSelectedCardPicker, setIsSelectedCardPicker] = useState(true)
  const [selectedCardPaymentId, setSelectedCardPaymentId] = useState()

  const {
    priceChargedNowDate,
    priceChargedNow,
    firstPayment,
    iframeUrl,
    iframeRef,
    loading: changeSubscriptionLoading,
    processing,
    changeSubscription,
    validCreditCards
  } = useChangeSubscription(changeDirection, product, voucher)

  const {
    onChange,
    onBlur,
    value,
    clear,
    onClick,
    enabled,
    error: passwordError,
    loading: passwordLoading
  } = usePassword(changeSubscription, selectedCardPaymentId)

  const isUpgradingPlan = changeDirection === direction.upgrade
  const isAnnuallyPlan = product?.type === planType.ANNUAL
  const hasSubscriptionCard = validCreditCards.some(card => card.useForSubscriptions)

  // Add payment date to details at specific position
  // Replace the payment date with today received from the server when the user is upgrading
  const paymentDetail = {
    name: 'Payment due',
    value: priceChargedNowDate ? `${priceChargedNowDate}` : `${firstPayment}`
  }
  const details = [...product.details]
  details.splice(1, 0, paymentDetail)

  // For upgrades, update payment amount to prorated charge if available
  if (isUpgradingPlan && priceChargedNow !== undefined) {
    const paymentIndex = details.findIndex(e => e.name === paymentLabel)
    if (paymentIndex !== -1) {
      details[paymentIndex].value = `$${priceChargedNow}`
    }
  }

  const pickerAndIframe =
    !(processing || passwordLoading) &&
    validCreditCards.length > 0 &&
    !!iframeRef &&
    !!iframeUrl

  const iframeOnly =
    !(processing || passwordLoading) &&
    validCreditCards.length === 0 &&
    !!iframeRef &&
    !!iframeUrl

  return changeSubscriptionLoading ? (
    <div className={styles.container}>
      <LoadingComponent pastDelay />
    </div>
  ) : (
    <div className={styles.container}>
      <PaymentMethodHeader
        label={product.title}
        detail={details}
        isStandardPlan={product.sku === standardPlan.sku}
        copy={product.copy}
        isUpgradingPlan={isUpgradingPlan}
        isAnnuallyPlan={isAnnuallyPlan}
        hasVoucher={Boolean(voucher)}
      />
      {(processing || passwordLoading) && <LoadingComponent pastDelay />}

      {pickerAndIframe && (
        <>
          <CreditCardChangeSubscription
            isSelected={isSelectedCardPicker}
            onRadioClick={() => setIsSelectedCardPicker(true)}
            setSelectedCardPaymentId={setSelectedCardPaymentId}
            selectedCardPaymentId={selectedCardPaymentId}
            validCreditCards={validCreditCards}
            clearInput={clear}
            error={passwordError}
            buttonOnClick={onClick}
            buttonDisabled={!enabled}
            onChange={onChange}
            onBlur={onBlur}
            value={value}
            buttonLabel="Confirm"
          />
          {/* Hide the "Use a different card" option when user already has card registered as subscription */}
          { !hasSubscriptionCard && (
            <IframeChangeSubscription
              useHeader
              iframeRef={iframeRef}
              iframeUrl={iframeUrl}
              isSelected={!isSelectedCardPicker}
              onRadioClick={() => setIsSelectedCardPicker(false)}
            />
          )}
        </>
      )}

      {iframeOnly && (
        <IframeChangeSubscription
          useHeader={false}
          iframeRef={iframeRef}
          iframeUrl={iframeUrl}
        />
      )}
    </div>
  )
}

Payment.propTypes = {
  product: PropTypes.shape({
    sku: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    details: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      })
    ),
    type: PropTypes.string,
    copy: PropTypes.string.isRequired
  }).isRequired,
  changeDirection: PropTypes.string.isRequired,
  voucher: PropTypes.string
}

Payment.defaultProps = {
  voucher: undefined
}

export default Payment
